/**
 * Redimensiona uma imagem para ajustar-se a uma largura e altura máximas, mantendo a proporção.
 *
 * @param {File} imagem - O arquivo da imagem a ser redimensionado.
 * @param {number} larguraMax - A largura máxima desejada.
 * @param {number} alturaMax - A altura máxima desejada.
 * @returns {Promise<{arquivo: File, preview: string}>} Uma Promise que resolve com um objeto contendo o arquivo redimensionado e a URL de visualização.
 */
function redimensionarImagem(imagem, larguraMax, alturaMax) {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = URL.createObjectURL(imagem);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const maxWidth = larguraMax;
      const maxHeight = alturaMax;
      let newWidth, newHeight;

      if (img.width / img.height > maxWidth / maxHeight) {
        newWidth = maxWidth;
        newHeight = (maxWidth / img.width) * img.height;
      } else {
        newHeight = maxHeight;
        newWidth = (maxHeight / img.height) * img.width;
      }

      canvas.width = newWidth;
      canvas.height = newHeight;

      ctx.clearRect(0, 0, newWidth, newHeight);
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob((blob) => {
        const resizedFile = new File([blob], imagem.name, {
          type: imagem.type,
          lastModified: Date.now(),
        });

        const resizedImageUrl = URL.createObjectURL(resizedFile);

        const imagemRedimensionada = {
          arquivo: resizedFile,
          preview: resizedImageUrl,
        };

        resolve(imagemRedimensionada);
      }, imagem.type);
    };
  });
}

/**
 * Corta uma imagem de forma centralizada para ajustar-se a uma largura e altura máximas.
 *
 * @param {File} imagem - O arquivo da imagem a ser cortado.
 * @param {number} larguraMax - A largura máxima desejada.
 * @param {number} alturaMax - A altura máxima desejada.
 * @returns {Promise<{arquivo: File, preview: string}>} Uma Promise que resolve com um objeto contendo o arquivo cortado e a URL de visualização.
 */
function cortarImagem(imagem, larguraMax, alturaMax) {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = URL.createObjectURL(imagem);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const originalWidth = img.width;
      const originalHeight = img.height;

      // Calcula proporções para redimensionar ou cortar centralizado
      const proporcaoLargura = larguraMax / originalWidth;
      const proporcaoAltura = alturaMax / originalHeight;
      const proporcao = Math.max(proporcaoLargura, proporcaoAltura);

      const newWidth = originalWidth * proporcao;
      const newHeight = originalHeight * proporcao;

      // Calcula coordenadas para cortar centralizado
      const x = (newWidth - larguraMax) / 2;
      const y = (newHeight - alturaMax) / 2;

      canvas.width = larguraMax;
      canvas.height = alturaMax;

      ctx.clearRect(0, 0, larguraMax, alturaMax);
      ctx.drawImage(img, -x, -y, newWidth, newHeight);

      canvas.toBlob((blob) => {
        const resizedFile = new File([blob], imagem.name, {
          type: imagem.type,
          lastModified: Date.now(),
        });

        const resizedImageUrl = URL.createObjectURL(resizedFile);

        const imagemRedimensionada = {
          arquivo: resizedFile,
          preview: resizedImageUrl,
        };

        resolve(imagemRedimensionada);
      }, imagem.type);
    };
  });
}

const DataProcess = { redimensionarImagem, cortarImagem };
export default DataProcess;
